// src/pages/FundPlanner/context/FundsContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import { getSharingPermissions, normalizeSharing } from "lib/permissions";
import useCurrentOwner from "components/FileManager/hooks/useCurrentOwner";
import {
  isCusipInvestmentType,
  normalizeInitialPositionAmount,
} from "../components/Funds/investmentTypes";
import {
  scheduledCashflowOrigin,
  scheduledCashflowOriginReference,
} from "../lib/scheduledCashflowActivity";
import { calculatePurchaseCost, roundToCents } from "../lib/money";

const FundsContext = createContext(null);

export function useFunds() {
  const ctx = useContext(FundsContext);
  if (!ctx) {
    throw new Error("useFunds must be used within a FundsProvider");
  }
  return ctx;
}

/* ────────────────────────────────
   Helpers
──────────────────────────────── */
function makeTempId() {
  return `pos_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function randomUuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();

  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (char) =>
    (
      Number(char) ^
      ((Math.random() * 16) >> (Number(char) / 4))
    ).toString(16)
  );
}

function trimToEmpty(value) {
  return String(value ?? "").trim();
}

function trimToNull(value) {
  const s = trimToEmpty(value);
  return s || null;
}

function normalizeCusip(value) {
  return trimToEmpty(value).toUpperCase();
}

function toNumberOrNull(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toIsoDateOrNull(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${y}-${m}-${d}`;
  }

  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, m, d, y] = slashMatch;
    return `${y}-${String(Number(m)).padStart(2, "0")}-${String(
      Number(d)
    ).padStart(2, "0")}`;
  }

  const dashUsMatch = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashUsMatch) {
    const [, m, d, y] = dashUsMatch;
    return `${y}-${String(Number(m)).padStart(2, "0")}-${String(
      Number(d)
    ).padStart(2, "0")}`;
  }

  return raw;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value ?? "")
  );
}

function buildLatestByKey(rows, key) {
  const map = new Map();

  for (const row of Array.isArray(rows) ? rows : []) {
    const value = row?.[key];
    if (!value || map.has(value)) continue;
    map.set(value, row);
  }

  return map;
}

function enrichScheduledCashflowRow(row, positionById) {
  const scheduledOrigin =
    row?.source_type === "system_scheduled_cashflow" &&
    row?.activity_type === "deposit"
      ? scheduledCashflowOrigin(row.external_activity_id)
      : null;
  const originPosition = scheduledOrigin
    ? positionById.get(scheduledOrigin.positionId)
    : null;

  return {
    ...row,
    scheduled_origin_cusip:
      originPosition?.cusip_or_id ||
      scheduledCashflowOriginReference(row?.notes) ||
      null,
    scheduled_event_kind: scheduledOrigin?.eventKind || null,
  };
}

function numberOrNull(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function compareValuationCandidate(a, b) {
  const dateCompare = String(a?.valuation_date || "").localeCompare(
    String(b?.valuation_date || "")
  );
  if (dateCompare) return dateCompare;

  return String(a?.created_at || "").localeCompare(String(b?.created_at || ""));
}

function latestPricedValuation(positionValuation, securityValuation) {
  const candidates = [];

  if (positionValuation?.price != null && positionValuation.price !== "") {
    candidates.push({
      source: "position",
      valuation_date: positionValuation.valuation_date || "",
      price: positionValuation.price,
      created_at: positionValuation.created_at || "",
    });
  }

  if (securityValuation?.price_mid != null && securityValuation.price_mid !== "") {
    candidates.push({
      source: "security_master",
      valuation_date: securityValuation.valuation_date || "",
      price: securityValuation.price_mid,
      created_at: securityValuation.created_at || "",
    });
  }

  const sorted = candidates.sort(compareValuationCandidate);
  return sorted[sorted.length - 1] || null;
}

function historyDate(row) {
  return row?.history_date || row?.valuation_date || row?.activity_date || "";
}

function compareHistoryDesc(a, b) {
  const dateCompare = String(historyDate(b)).localeCompare(String(historyDate(a)));
  if (dateCompare) return dateCompare;

  return String(b?.created_at || "").localeCompare(String(a?.created_at || ""));
}

function isAfterHistoryAnchor(row, anchor) {
  if (!anchor) return true;

  const dateCompare = String(historyDate(row)).localeCompare(
    String(historyDate(anchor))
  );
  if (dateCompare) return dateCompare > 0;

  return String(row?.created_at || "").localeCompare(
    String(anchor?.created_at || "")
  ) > 0;
}

export function derivePositionAmountsFromHistory(row = {}, historyRows = []) {
  const rows = Array.isArray(historyRows) ? historyRows : [];
  const latestValuation =
    rows
      .filter((entry) => entry?.history_type === "valuation" || entry?.valuation_date)
      .sort(compareHistoryDesc)[0] || null;
  const initialPositionValuation =
    rows
      .filter((entry) => entry?.source_type === "initial_position")
      .filter(
        (entry) =>
          numberOrNull(entry.current_face) !== null ||
          numberOrNull(entry.par_amount) !== null ||
          numberOrNull(entry.units) !== null ||
          numberOrNull(entry.balance_amount) !== null
      )
      .sort(compareHistoryDesc)[0] || null;
  const amountAnchor =
    rows
      .filter(
        (entry) =>
          (entry?.history_type === "valuation" || entry?.valuation_date) &&
          entry?.source_type !== "initial_position" &&
          (numberOrNull(entry.current_face) !== null ||
            numberOrNull(entry.units) !== null ||
            numberOrNull(entry.balance_amount) !== null)
      )
      .sort(compareHistoryDesc)[0] || null;

  let currentFace =
    numberOrNull(amountAnchor?.current_face) ??
    numberOrNull(initialPositionValuation?.current_face) ??
    numberOrNull(initialPositionValuation?.par_amount) ??
    numberOrNull(row.par_amount) ??
    numberOrNull(row.current_face);
  let units =
    numberOrNull(amountAnchor?.units) ??
    numberOrNull(initialPositionValuation?.units) ??
    numberOrNull(initialPositionValuation?.par_amount) ??
    numberOrNull(row.par_amount) ??
    numberOrNull(row.units);
  let balanceAmount =
    numberOrNull(amountAnchor?.balance_amount) ??
    numberOrNull(initialPositionValuation?.balance_amount) ??
    numberOrNull(initialPositionValuation?.units) ??
    numberOrNull(initialPositionValuation?.par_amount) ??
    numberOrNull(row.par_amount) ??
    numberOrNull(row.balance_amount);
  balanceAmount = roundToCents(balanceAmount);

  rows
    .filter((entry) => entry?.history_type === "activity" || entry?.activity_date)
    .filter((entry) => isAfterHistoryAnchor(entry, amountAnchor))
    .sort((a, b) => -compareHistoryDesc(a, b))
    .forEach((entry) => {
      const parAfter = numberOrNull(entry.par_after);
      const unitsAfter = numberOrNull(entry.units_after);
      const balanceAfter = numberOrNull(entry.balance_after);
      const parDelta = numberOrNull(entry.par_delta);
      const unitsDelta = numberOrNull(entry.units_delta);

      if (parAfter !== null) {
        currentFace = parAfter;
      } else if (parDelta !== null && currentFace !== null) {
        currentFace += parDelta;
      }

      if (unitsAfter !== null) {
        units = unitsAfter;
      } else if (unitsDelta !== null && units !== null) {
        units += unitsDelta;
      }

      if (balanceAfter !== null) {
        balanceAmount = roundToCents(balanceAfter);
      } else if (unitsDelta !== null && balanceAmount !== null) {
        balanceAmount = roundToCents(balanceAmount + roundToCents(unitsDelta));
      }
    });

  return {
    latestValuation,
    currentFace,
    units,
    balanceAmount,
  };
}

function latestRatingValuation(historyRows = []) {
  return (
    (Array.isArray(historyRows) ? historyRows : [])
      .filter((entry) => entry?.history_type === "valuation" || entry?.valuation_date)
      .filter((entry) => trimToEmpty(entry?.rating_rollup))
      .sort(compareHistoryDesc)[0] || null
  );
}

function isSaleActivity(row) {
  return (
    row?.activity_type === "sale" ||
    row?.activity_type === "partial_sale" ||
    row?.activity_type === "redemption" ||
    row?.activity_type === "principal_paydown"
  );
}

function latestSaleDate(historyRows = []) {
  const sale = (Array.isArray(historyRows) ? historyRows : [])
    .filter((row) => row?.history_type === "activity" || row?.activity_date)
    .filter(isSaleActivity)
    .sort(compareHistoryDesc)[0];

  return sale?.activity_date || sale?.history_date || "";
}

function isZeroish(value) {
  const n = Number(value);
  return Number.isFinite(n) && Math.abs(n) < 0.005;
}

function isZeroBalance(row, derivedAmounts) {
  const amount = isCusipPositionType(row?.investment_type)
    ? derivedAmounts.currentFace
    : derivedAmounts.balanceAmount ?? derivedAmounts.units;

  return isZeroish(amount);
}

function isCusipPositionType(value) {
  return isCusipInvestmentType(trimToEmpty(value));
}

function isCashPositionType(value) {
  return trimToEmpty(value).toLowerCase() === "cash";
}

function isSlgsDemandDepositType(value) {
  const type = trimToEmpty(value).toLowerCase();
  return type === "slgs demand deposit" || type === "demand deposit";
}

function defaultPurchasePriceForType(investmentType, value = "") {
  const type = trimToEmpty(investmentType);
  return type && !isCusipPositionType(type) && value === "" ? 100 : value;
}

export function makeBlankPosition() {
  return {
    id: makeTempId(),
    investment_type: "",
    cusip_or_id: "",
    par_amount: "",
    current_face: "",
    units: "",
    balance_amount: "",
    purchase_price: "",
    purchase_accrued_interest: "",
    rate: "",
    day_count_basis: "",
    interest_frequency: "",
    maturity_date: "",
    purchase_date: "",
    settlement_date: "",
    closed_date: "",
    issuer: "",
    description: "",
    notes: "",
    initial_rating_rollup: "",
    initial_rating_rollup_explicit: false,
    source_of_funds_position_id: "",
    __isNew: true,
  };
}

export function buildPositionFromDb(row = {}) {
  return {
    id: row.id || makeTempId(),
    investment_type: row.investment_type || "",
    cusip_or_id: row.cusip_or_id || "",
    par_amount: row.par_amount ?? "",
    current_face: row.current_face ?? "",
    units: row.units ?? "",
    balance_amount: row.balance_amount ?? "",
    latest_valuation: row.latest_valuation ?? null,
    latest_rating_valuation: row.latest_rating_valuation ?? null,
    latest_security_master_valuation: row.latest_security_master_valuation ?? null,
    latest_priced_valuation: row.latest_priced_valuation ?? null,
    security_maturity_date: row.security_maturity_date || "",
    purchase_price: row.purchase_price ?? "",
    original_cost: row.original_cost ?? null,
    purchase_accrued_interest: row.purchase_accrued_interest ?? "",
    rate: row.raw_coupon ?? "",
    day_count_basis: row.day_count_basis || "",
    interest_frequency: row.interest_frequency || "",
    maturity_date: row.raw_maturity_date || "",
    purchase_date: row.purchase_date || "",
    settlement_date: row.settlement_date || "",
    closed_date: row.closed_date || "",
    issuer: row.raw_issuer_name || "",
    description: row.raw_security_description || "",
    notes: row.notes || "",
    initial_rating_rollup: row.initial_rating_rollup || "",
    initial_rating_rollup_explicit: false,
    source_of_funds_position_id: row.source_of_funds_position_id || "",
    __isNew: false,
  };
}

function isBlankPosition(row) {
  if (!row) return true;

  return ![
    row.investment_type,
    row.cusip_or_id,
    row.par_amount,
    row.current_face,
    row.units,
    row.balance_amount,
    row.purchase_price,
    row.purchase_accrued_interest,
    row.rate,
    row.day_count_basis,
    row.interest_frequency,
    row.maturity_date,
    row.purchase_date,
    row.settlement_date,
    row.closed_date,
    row.issuer,
    row.description,
    row.notes,
    row.initial_rating_rollup,
  ].some((v) => String(v ?? "").trim() !== "");
}

function validatePosition(row, rowNumber) {
  if (isBlankPosition(row)) return null;

  if (!trimToEmpty(row.investment_type)) {
    return `Position row ${rowNumber} is missing Type.`;
  }

  if (!trimToEmpty(row.cusip_or_id)) {
    return `Position row ${rowNumber} is missing CUSIP / ID.`;
  }

  if (trimToEmpty(row.par_amount) === "") {
    return `Position row ${rowNumber} is missing Amount.`;
  }

  const amount = Number(row.par_amount);
  if (!Number.isFinite(amount) || amount < 0) {
    return `Position row ${rowNumber} Amount must be a non-negative number.`;
  }

  if (trimToEmpty(row.purchase_price) === "") {
    return `Position row ${rowNumber} is missing Purchase Price.`;
  }

  const purchasePrice = Number(row.purchase_price);
  if (!Number.isFinite(purchasePrice) || purchasePrice <= 0) {
    return `Position row ${rowNumber} Purchase Price must be a positive number.`;
  }

  const purchaseAccruedInterest = toNumberOrNull(row.purchase_accrued_interest);
  if (purchaseAccruedInterest != null && purchaseAccruedInterest < 0) {
    return `Position row ${rowNumber} Accrued Interest must be a non-negative number.`;
  }

  if (!toIsoDateOrNull(row.purchase_date)) {
    return `Position row ${rowNumber} is missing Trade Date.`;
  }

  if (!toIsoDateOrNull(row.settlement_date)) {
    return `Position row ${rowNumber} is missing Settlement Date.`;
  }

  return null;
}

function normalizeUiPosition(row = {}) {
  const investmentType = trimToEmpty(row.investment_type);
  const initialAmount = normalizeInitialPositionAmount(
    investmentType,
    row.par_amount
  );

  return {
    id: row.id || makeTempId(),
    investment_type: investmentType,
    cusip_or_id: trimToEmpty(row.cusip_or_id),
    par_amount: initialAmount ?? "",
    current_face: row.current_face ?? "",
    units: row.units ?? "",
    balance_amount: row.balance_amount ?? "",
    latest_valuation: row.latest_valuation ?? null,
    latest_rating_valuation: row.latest_rating_valuation ?? null,
    latest_security_master_valuation: row.latest_security_master_valuation ?? null,
    latest_priced_valuation: row.latest_priced_valuation ?? null,
    security_maturity_date: row.security_maturity_date || "",
    purchase_price: defaultPurchasePriceForType(
      investmentType,
      row.purchase_price ?? ""
    ),
    original_cost: row.original_cost ?? null,
    purchase_accrued_interest: row.purchase_accrued_interest ?? "",
    rate: row.rate ?? "",
    day_count_basis: trimToEmpty(row.day_count_basis),
    interest_frequency: trimToEmpty(row.interest_frequency),
    maturity_date: toIsoDateOrNull(row.maturity_date) || "",
    purchase_date: toIsoDateOrNull(row.purchase_date) || "",
    settlement_date: toIsoDateOrNull(row.settlement_date) || "",
    closed_date: toIsoDateOrNull(row.closed_date) || "",
    issuer: trimToEmpty(row.issuer),
    description: trimToEmpty(row.description),
    notes: trimToEmpty(row.notes),
    initial_rating_rollup: trimToEmpty(row.initial_rating_rollup),
    initial_rating_rollup_explicit: !!row.initial_rating_rollup_explicit,
    source_of_funds_position_id: trimToEmpty(
      row.source_of_funds_position_id
    ),
    __isNew: !!row.__isNew || !isUuid(row.id),
  };
}

export function positionSaveSnapshot(row = {}) {
  const investmentType = trimToEmpty(row.investment_type);

  return {
    investment_type: investmentType,
    cusip_or_id: trimToEmpty(row.cusip_or_id),
    par_amount: toNumberOrNull(
      normalizeInitialPositionAmount(investmentType, row.par_amount)
    ),
    purchase_price: toNumberOrNull(
      defaultPurchasePriceForType(investmentType, row.purchase_price)
    ),
    purchase_accrued_interest: isCusipPositionType(investmentType)
      ? toNumberOrNull(row.purchase_accrued_interest)
      : null,
    rate: toNumberOrNull(row.rate),
    day_count_basis: trimToNull(row.day_count_basis),
    interest_frequency: trimToNull(row.interest_frequency),
    maturity_date: toIsoDateOrNull(row.maturity_date),
    purchase_date: toIsoDateOrNull(row.purchase_date),
    settlement_date: toIsoDateOrNull(row.settlement_date),
    closed_date: toIsoDateOrNull(row.closed_date),
    issuer: trimToNull(row.issuer),
    description: trimToNull(row.description),
    notes: trimToNull(row.notes),
    initial_rating_rollup: trimToNull(row.initial_rating_rollup),
    source_of_funds_position_id: trimToNull(
      row.source_of_funds_position_id
    ),
  };
}

export function hasPositionChanged(currentRow = {}, originalDbRow = {}) {
  if (!originalDbRow?.id) return true;

  const currentSnapshot = positionSaveSnapshot(currentRow);
  const originalSnapshot = positionSaveSnapshot(
    normalizeUiPosition(buildPositionFromDb(originalDbRow))
  );

  return Object.keys(currentSnapshot).some(
    (key) => currentSnapshot[key] !== originalSnapshot[key]
  );
}

function positionToDbPayload({
  row,
  fundId,
  orgId,
  tenantId,
  userId,
  preserve = {},
}) {
  const closedDate = toIsoDateOrNull(row.closed_date);
  const payload = {
    org_id: orgId,
    tenant_id: tenantId,
    fund_id: fundId,
    created_by: preserve.created_by || userId,
    source_type: preserve.source_type || "manual",
    source_batch_id: preserve.source_batch_id ?? null,
    source_file_row_num: preserve.source_file_row_num ?? null,
    external_position_id: preserve.external_position_id ?? null,
    lot_id: preserve.lot_id ?? null,
    cusip_or_id: trimToEmpty(row.cusip_or_id),
    purchase_date: toIsoDateOrNull(row.purchase_date),
    settlement_date: toIsoDateOrNull(row.settlement_date),
    closed_date: closedDate,
    par_amount: toNumberOrNull(row.par_amount),
    current_face: preserve.current_face ?? toNumberOrNull(row.current_face),
    units: preserve.units ?? toNumberOrNull(row.units),
    purchase_price: toNumberOrNull(
      defaultPurchasePriceForType(row.investment_type, row.purchase_price)
    ),
    purchase_accrued_interest: isCusipPositionType(row.investment_type)
      ? toNumberOrNull(row.purchase_accrued_interest)
      : null,
    original_cost: isCusipPositionType(row.investment_type)
      ? roundToCents(row.original_cost) ??
        calculatePurchaseCost(row.par_amount, row.purchase_price)
      : preserve.original_cost ?? null,
    book_value_input: preserve.book_value_input ?? null,
    market_value_input: preserve.market_value_input ?? null,
    account_name: preserve.account_name ?? null,
    account_number: preserve.account_number ?? null,
    fund_account_code: preserve.fund_account_code ?? null,
    raw_security_description: trimToNull(row.description),
    raw_security_type: preserve.raw_security_type ?? null,
    raw_coupon: toNumberOrNull(row.rate),
    day_count_basis: trimToNull(row.day_count_basis),
    interest_frequency: trimToNull(row.interest_frequency),
    raw_maturity_date: toIsoDateOrNull(row.maturity_date),
    raw_issuer_name: trimToNull(row.issuer),
    is_active: !closedDate,
    notes: trimToNull(row.notes),
    investment_type: trimToEmpty(row.investment_type) || "marketable_security",
    source_of_funds_position_id:
      trimToEmpty(row.source_of_funds_position_id) || null,
  };

  return payload;
}

export function purchaseFundingAmount(row = {}) {
  const amount = toNumberOrNull(row.par_amount);
  if (amount == null) return null;
  if (!isCusipPositionType(row.investment_type)) return Math.abs(amount);
  const price = toNumberOrNull(row.purchase_price);
  if (price == null) return null;
  const accruedInterest = toNumberOrNull(row.purchase_accrued_interest) ?? 0;
  const purchaseCost =
    roundToCents(row.original_cost) ?? calculatePurchaseCost(amount, price);
  if (purchaseCost == null) return null;
  const accruedInterestCost = roundToCents(Math.abs(accruedInterest)) ?? 0;
  return roundToCents(Math.abs(purchaseCost) + accruedInterestCost);
}

async function syncPositionPurchaseFunding({
  row,
  orgId,
  tenantId,
  userId,
  profile,
}) {
  const fundedPositionId = row.id;
  const sourcePositionId = trimToEmpty(row.source_of_funds_position_id);

  const { data: existingRows, error: existingError } = await supabase
    .from("fp_position_activity")
    .select("id")
    .eq("org_id", orgId)
    .eq("source_type", "position_purchase_funding")
    .eq("funded_position_id", fundedPositionId);
  if (existingError) throw existingError;
  const existingId = existingRows?.[0]?.id || "";

  if (!sourcePositionId) {
    if (existingId) {
      const { error } = await supabase
        .from("fp_position_activity")
        .delete()
        .eq("id", existingId)
        .eq("org_id", orgId);
      if (error) throw error;
    }
    return;
  }

  if (!isUuid(sourcePositionId) || sourcePositionId === fundedPositionId) {
    throw new Error("Select a valid source of funds position.");
  }

  const amount = purchaseFundingAmount(row);
  if (amount == null || amount <= 0) {
    throw new Error(
      `Unable to calculate purchase funding for ${row.cusip_or_id || "position"}.`
    );
  }

  const { data: sourcePosition, error: sourceError } = await supabase
    .from("fp_positions")
    .select("id,fund_id,investment_type")
    .eq("id", sourcePositionId)
    .eq("org_id", orgId)
    .single();
  if (sourceError) throw sourceError;
  if (!isTransferEligiblePositionType(sourcePosition.investment_type)) {
    throw new Error("Source of funds must be a Cash, LGIP, Money Market Fund, or Bank Deposit position.");
  }

  const { data: sourceFund, error: fundError } = await supabase
    .from("fp_investment_funds")
    .select("id,created_by,sharing")
    .eq("id", sourcePosition.fund_id)
    .eq("org_id", orgId)
    .single();
  if (fundError) throw fundError;
  if (!getSharingPermissions(sourceFund, profile).canEdit) {
    throw new Error("You must be able to edit the source-of-funds account.");
  }

  const activity = {
    ...activityToDbPayload({
      payload: {
        activity_type: "purchase",
        activity_date:
          toIsoDateOrNull(row.settlement_date) ||
          toIsoDateOrNull(row.purchase_date),
        units_delta: -amount,
        cash_amount: -amount,
        source_type: "position_purchase_funding",
        external_activity_id: `purchase-funding:${fundedPositionId}`,
        notes: `Purchase Funding – ${row.cusip_or_id || "Position"}`,
      },
      fundId: sourcePosition.fund_id,
      orgId,
      tenantId,
      positionId: sourcePositionId,
      userId,
    }),
    funded_position_id: fundedPositionId,
  };

  const query = existingId
    ? supabase.from("fp_position_activity").update(activity).eq("id", existingId)
    : supabase.from("fp_position_activity").insert(activity);
  const { error } = await query;
  if (error) throw error;
}

function isMissingPositionValuationRatingColumnError(error) {
  const message = String(error?.message || error?.details || "").toLowerCase();
  return message.includes("rating_rollup") && message.includes("column");
}

function reinvestmentGroupId(value) {
  const raw = trimToEmpty(value);
  return raw.replace(/:(income|posting)$/i, "");
}

function linkedActivityGroupId(value) {
  const raw = trimToEmpty(value);
  return raw.replace(/:(income|posting|out|in)$/i, "");
}

function isTransferEligiblePositionType(value) {
  const normalized = trimToEmpty(value).toLowerCase();
  return (
    normalized === "bank deposit" ||
    normalized === "cash" ||
    normalized === "lgip" ||
    normalized === "mmf" ||
    normalized === "money market fund" ||
    normalized === "money market funds" ||
    normalized === "money market"
  );
}

function ensureActivityValuationContext({
  positionId,
  currentId,
  selectedOrg,
  profile,
}) {
  if (!selectedOrg?.id || !profile?.id || !profile?.tenant_id || !currentId) {
    return "Missing org, user, or fund context.";
  }

  if (!isUuid(positionId)) {
    return "Save the fund before recording activity or valuation updates.";
  }

  return "";
}

function activityToDbPayload({
  payload = {},
  fundId,
  orgId,
  tenantId,
  positionId,
  userId,
}) {
  return {
    org_id: orgId,
    tenant_id: tenantId,
    fund_id: fundId,
    position_id: positionId,
    created_by: userId,
    activity_type: trimToNull(payload.activity_type),
    activity_date: toIsoDateOrNull(payload.activity_date),
    trade_date: toIsoDateOrNull(payload.trade_date),
    settlement_date: toIsoDateOrNull(payload.settlement_date),
    par_delta: toNumberOrNull(payload.par_delta),
    units_delta: toNumberOrNull(payload.units_delta),
    cash_amount: toNumberOrNull(payload.cash_amount),
    price: toNumberOrNull(payload.price),
    par_after: toNumberOrNull(payload.par_after),
    units_after: toNumberOrNull(payload.units_after),
    balance_after: toNumberOrNull(payload.balance_after),
    income_amount: toNumberOrNull(payload.income_amount),
    income_period_start: toIsoDateOrNull(payload.income_period_start),
    income_period_end: toIsoDateOrNull(payload.income_period_end),
    realized_gain_loss: toNumberOrNull(payload.realized_gain_loss),
    book_value_relieved: toNumberOrNull(payload.book_value_relieved),
    accrued_interest_sold: toNumberOrNull(payload.accrued_interest_sold),
    source_type: trimToEmpty(payload.source_type) || "manual",
    source_batch_id: trimToNull(payload.source_batch_id),
    source_file_row_num: payload.source_file_row_num ?? null,
    external_activity_id: trimToNull(payload.external_activity_id),
    notes: trimToNull(payload.notes),
  };
}

function valuationToDbPayload({
  payload = {},
  fundId,
  orgId,
  tenantId,
  positionId,
  userId,
}) {
  const dbPayload = {
    org_id: orgId,
    tenant_id: tenantId,
    fund_id: fundId,
    position_id: positionId,
    created_by: userId,
    valuation_date: toIsoDateOrNull(payload.valuation_date),
    par_amount: toNumberOrNull(payload.par_amount),
    current_face: toNumberOrNull(payload.current_face),
    units: toNumberOrNull(payload.units),
    balance_amount: toNumberOrNull(payload.balance_amount),
    price: toNumberOrNull(payload.price),
    book_value: toNumberOrNull(payload.book_value),
    market_value: toNumberOrNull(payload.market_value),
    accrued_interest: toNumberOrNull(payload.accrued_interest),
    effective_yield_override: toNumberOrNull(payload.effective_yield_override),
    effective_yield_start_date: toIsoDateOrNull(payload.effective_yield_start_date),
    effective_yield_end_date: toIsoDateOrNull(payload.effective_yield_end_date),
    source_type: trimToEmpty(payload.source_type) || "manual",
    is_override: !!payload.is_override,
    notes: trimToNull(payload.notes),
  };

  if (Object.prototype.hasOwnProperty.call(payload, "rating_rollup")) {
    dbPayload.rating_rollup = trimToNull(payload.rating_rollup);
  }

  return dbPayload;
}

function initialPositionValuationToDbPayload({
  row,
  positionId,
  fundId,
  orgId,
  tenantId,
  userId,
}) {
  const valuationDate =
    toIsoDateOrNull(row.settlement_date) ||
    toIsoDateOrNull(row.purchase_date) ||
    todayIso();
  const amount = row.par_amount;
  const isCusipType = isCusipPositionType(row.investment_type);

  return valuationToDbPayload({
    payload: {
      valuation_date: valuationDate,
      par_amount: amount,
      current_face: isCusipType ? amount : "",
      units: isCusipType ? "" : amount,
      balance_amount: isCusipType ? "" : amount,
      price: isCusipType ? row.purchase_price : "",
      ...(row.initial_rating_rollup_explicit ||
      trimToEmpty(row.initial_rating_rollup)
        ? { rating_rollup: row.initial_rating_rollup }
        : {}),
      source_type: "initial_position",
      is_override: false,
    },
    fundId,
    orgId,
    tenantId,
    positionId,
    userId,
  });
}

async function syncInitialPositionValuation({
  row,
  originalRow = {},
  positionId,
  fundId,
  orgId,
  tenantId,
  userId,
}) {
  const payload = initialPositionValuationToDbPayload({
    row,
    positionId,
    fundId,
    orgId,
    tenantId,
    userId,
  });
  const nextDate = payload.valuation_date;
  const previousDate =
    toIsoDateOrNull(originalRow.settlement_date) ||
    toIsoDateOrNull(originalRow.purchase_date) ||
    "";
  const { data: existingInitialRows, error: existingInitialError } = await supabase
    .from("fp_position_valuations")
    .select("*")
    .eq("position_id", positionId)
    .eq("fund_id", fundId)
    .eq("org_id", orgId)
    .eq("source_type", "initial_position")
    .order("valuation_date", { ascending: false })
    .order("created_at", { ascending: false });

  if (existingInitialError) throw existingInitialError;

  const existingInitial = Array.isArray(existingInitialRows)
    ? existingInitialRows[0]
    : null;

  if (
    !row.initial_rating_rollup_explicit &&
    !trimToEmpty(row.initial_rating_rollup) &&
    existingInitial?.rating_rollup
  ) {
    payload.rating_rollup = existingInitial.rating_rollup;
  }

  if (previousDate && previousDate !== nextDate) {
    const { error: deleteOldError } = await supabase
      .from("fp_position_valuations")
      .delete()
      .eq("position_id", positionId)
      .eq("fund_id", fundId)
      .eq("org_id", orgId)
      .eq("source_type", "initial_position")
      .eq("valuation_date", previousDate);

    if (deleteOldError) throw deleteOldError;
  }

  const { error } = await supabase
    .from("fp_position_valuations")
    .upsert(payload, {
      onConflict: "position_id,valuation_date,source_type",
    });

  if (error) throw error;
}

function activityUpdatePayload(payload = {}) {
  return {
    activity_type: trimToNull(payload.activity_type),
    activity_date: toIsoDateOrNull(payload.activity_date),
    trade_date: toIsoDateOrNull(payload.trade_date),
    settlement_date: toIsoDateOrNull(payload.settlement_date),
    par_delta: toNumberOrNull(payload.par_delta),
    units_delta: toNumberOrNull(payload.units_delta),
    cash_amount: toNumberOrNull(payload.cash_amount),
    price: toNumberOrNull(payload.price),
    par_after: toNumberOrNull(payload.par_after),
    units_after: toNumberOrNull(payload.units_after),
    balance_after: toNumberOrNull(payload.balance_after),
    income_amount: toNumberOrNull(payload.income_amount),
    income_period_start: toIsoDateOrNull(payload.income_period_start),
    income_period_end: toIsoDateOrNull(payload.income_period_end),
    realized_gain_loss: toNumberOrNull(payload.realized_gain_loss),
    book_value_relieved: toNumberOrNull(payload.book_value_relieved),
    accrued_interest_sold: toNumberOrNull(payload.accrued_interest_sold),
    source_type: trimToEmpty(payload.source_type) || "manual",
    source_batch_id: trimToNull(payload.source_batch_id),
    source_file_row_num: payload.source_file_row_num ?? null,
    external_activity_id: trimToNull(payload.external_activity_id),
    notes: trimToNull(payload.notes),
  };
}

function valuationUpdatePayload(payload = {}) {
  const dbPayload = {
    valuation_date: toIsoDateOrNull(payload.valuation_date),
    par_amount: toNumberOrNull(payload.par_amount),
    current_face: toNumberOrNull(payload.current_face),
    units: toNumberOrNull(payload.units),
    balance_amount: toNumberOrNull(payload.balance_amount),
    price: toNumberOrNull(payload.price),
    book_value: toNumberOrNull(payload.book_value),
    market_value: toNumberOrNull(payload.market_value),
    accrued_interest: toNumberOrNull(payload.accrued_interest),
    effective_yield_override: toNumberOrNull(payload.effective_yield_override),
    effective_yield_start_date: toIsoDateOrNull(payload.effective_yield_start_date),
    effective_yield_end_date: toIsoDateOrNull(payload.effective_yield_end_date),
    source_type: trimToEmpty(payload.source_type) || "manual",
    is_override: !!payload.is_override,
    notes: trimToNull(payload.notes),
  };

  if (Object.prototype.hasOwnProperty.call(payload, "rating_rollup")) {
    dbPayload.rating_rollup = trimToNull(payload.rating_rollup);
  }

  return dbPayload;
}

function cashActivityAmount(payload = {}) {
  const type = trimToEmpty(payload.activity_type);
  const sourceType = trimToEmpty(payload.source_type);

  if (sourceType === "manual_interest_reinvestment" || sourceType === "manual_transfer") {
    return null;
  }

  if (type === "interest_income" || type === "dividend_income") {
    const parDelta = toNumberOrNull(payload.par_delta);
    const unitsDelta = toNumberOrNull(payload.units_delta);
    if (
      (parDelta != null && parDelta > 0) ||
      (unitsDelta != null && unitsDelta > 0)
    ) {
      return null;
    }

    return (
      toNumberOrNull(payload.income_amount) ??
      toNumberOrNull(payload.cash_amount)
    );
  }

  if (
    type === "redemption" ||
    type === "principal_paydown" ||
    type === "sale" ||
    type === "partial_sale"
  ) {
    const cash = toNumberOrNull(payload.cash_amount);
    if (cash != null) return Math.abs(cash);

    const par = toNumberOrNull(payload.par_delta);
    if (par != null && par !== 0) return Math.abs(par);

    const units = toNumberOrNull(payload.units_delta);
    if (units != null && units !== 0) return Math.abs(units);
  }

  return null;
}

function isManualTransferActivity(row = {}) {
  return row?.source_type === "manual_transfer" && row?.external_activity_id;
}

export function transferCounterpartyLabel({ row = {}, counterparty } = {}) {
  const safeCounterparty = counterparty || {};
  const parts = [];

  if (
    safeCounterparty.fund_name &&
    safeCounterparty.fund_id !== row.fund_id
  ) {
    parts.push(safeCounterparty.fund_name);
  }

  parts.push(safeCounterparty.cusip_or_id || "unpaired position");
  return parts.join(" ");
}

async function enrichManualTransferRows({ rows = [], orgId }) {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const transferRows = sourceRows.filter(isManualTransferActivity);
  if (!transferRows.length || !orgId) return sourceRows;

  const transferIds = Array.from(
    new Set(
      transferRows
        .map((row) => linkedActivityGroupId(row.external_activity_id))
        .filter(Boolean)
    )
  );
  const externalActivityIds = transferIds.flatMap((id) => [
    `${id}:out`,
    `${id}:in`,
  ]);

  const { data: pairRows, error: pairError } = await supabase
    .from("fp_position_activity")
    .select("*")
    .eq("org_id", orgId)
    .eq("source_type", "manual_transfer")
    .in("external_activity_id", externalActivityIds);

  if (pairError) throw pairError;

  const activities = Array.isArray(pairRows) ? pairRows : [];
  const positionIds = Array.from(
    new Set(activities.map((row) => row.position_id).filter(Boolean))
  );

  let positionById = new Map();
  if (positionIds.length) {
    const { data: positions, error: positionsError } = await supabase
      .from("fp_positions")
      .select("id,fund_id,cusip_or_id,investment_type,raw_security_description")
      .eq("org_id", orgId)
      .in("id", positionIds);

    if (positionsError) throw positionsError;
    positionById = new Map(
      (Array.isArray(positions) ? positions : []).map((position) => [
        position.id,
        position,
      ])
    );
  }

  const fundIds = Array.from(
    new Set(
      Array.from(positionById.values())
        .map((position) => position.fund_id)
        .filter(Boolean)
    )
  );

  let fundById = new Map();
  if (fundIds.length) {
    const { data: funds, error: fundsError } = await supabase
      .from("fp_investment_funds")
      .select("id,name,portfolio_id")
      .eq("org_id", orgId)
      .in("id", fundIds);

    if (fundsError) throw fundsError;
    fundById = new Map(
      (Array.isArray(funds) ? funds : []).map((fund) => [fund.id, fund])
    );
  }

  const portfolioIds = Array.from(
    new Set(
      Array.from(fundById.values())
        .map((fund) => fund.portfolio_id)
        .filter(Boolean)
    )
  );

  let portfolioById = new Map();
  if (portfolioIds.length) {
    const { data: portfolios, error: portfoliosError } = await supabase
      .from("fp_portfolios")
      .select("id,name")
      .eq("org_id", orgId)
      .in("id", portfolioIds);

    if (portfoliosError) throw portfoliosError;
    portfolioById = new Map(
      (Array.isArray(portfolios) ? portfolios : []).map((portfolio) => [
        portfolio.id,
        portfolio,
      ])
    );
  }

  const decorateActivity = (activity) => {
    const position = positionById.get(activity?.position_id) || {};
    const fund = fundById.get(position.fund_id) || {};
    const portfolio = portfolioById.get(fund.portfolio_id) || {};
    return {
      ...activity,
      cusip_or_id: position.cusip_or_id || "",
      position_investment_type: position.investment_type || "",
      fund_id: position.fund_id || activity?.fund_id || "",
      fund_name: fund.name || "",
      portfolio_id: fund.portfolio_id || "",
      portfolio_name: portfolio.name || "",
    };
  };

  const decoratedActivities = activities.map(decorateActivity);
  const activitiesByGroup = new Map();
  decoratedActivities.forEach((activity) => {
    const groupId = linkedActivityGroupId(activity.external_activity_id);
    const group = activitiesByGroup.get(groupId) || [];
    group.push(activity);
    activitiesByGroup.set(groupId, group);
  });

  return sourceRows.map((row) => {
    if (!isManualTransferActivity(row)) return row;

    const decoratedRow = decorateActivity(row);
    const group = activitiesByGroup.get(
      linkedActivityGroupId(row.external_activity_id)
    ) || [];
    const counterparty =
      group.find((activity) => activity.id !== row.id) ||
      group.find((activity) => activity.position_id !== row.position_id) ||
      null;

    return {
      ...decoratedRow,
      transfer_counterparty: counterparty,
      transfer_action_label: transferCounterpartyLabel({
        row: decoratedRow,
        counterparty,
      }),
    };
  });
}

async function getOrCreateCashPosition({
  fundId,
  orgId,
  tenantId,
  userId,
  activityDate,
}) {
  const { data: cashRows, error: cashRowsError } = await supabase
    .from("fp_positions")
    .select("*")
    .eq("fund_id", fundId)
    .eq("org_id", orgId)
    .ilike("investment_type", "Cash")
    .is("closed_date", null)
    .order("created_at", { ascending: true });

  if (cashRowsError) throw cashRowsError;

  const activeCashRows = Array.isArray(cashRows) ? cashRows : [];
  if (activeCashRows.length > 1) {
    throw new Error(
      "This fund has more than one open Cash position. Keep a single Cash position before posting cash activity."
    );
  }

  if (activeCashRows.length === 1) {
    return activeCashRows[0];
  }

  const date = toIsoDateOrNull(activityDate) || todayIso();
  const { data: insertedCash, error: insertError } = await supabase
    .from("fp_positions")
    .insert({
      org_id: orgId,
      tenant_id: tenantId,
      fund_id: fundId,
      created_by: userId,
      source_type: "system_cash_sweep",
      cusip_or_id: "CASH",
      purchase_date: date,
      settlement_date: date,
      par_amount: 0,
      current_face: null,
      units: 0,
      purchase_price: 100,
      raw_security_description: "Uninvested Cash",
      raw_issuer_name: "Cash",
      is_active: true,
      investment_type: "Cash",
      notes: "Automatically created for fund cash activity.",
    })
    .select("*")
    .single();

  if (insertError) throw insertError;

  const { error: valuationError } = await supabase
    .from("fp_position_valuations")
    .insert(
      valuationToDbPayload({
        payload: {
          valuation_date: date,
          units: 0,
          balance_amount: 0,
          price: 100,
          source_type: "initial_position",
          notes: "Initial Cash position balance.",
        },
        fundId,
        orgId,
        tenantId,
        positionId: insertedCash.id,
        userId,
      })
    );

  if (valuationError) throw valuationError;

  return insertedCash;
}

async function postCashOffsetActivity({
  sourcePositionId,
  sourceActivityPayload,
  fundId,
  orgId,
  tenantId,
  userId,
}) {
  const amount = cashActivityAmount(sourceActivityPayload);
  if (amount == null || amount <= 0) return null;

  const { data: sourcePosition, error: sourceError } = await supabase
    .from("fp_positions")
    .select("id, investment_type, cusip_or_id")
    .eq("id", sourcePositionId)
    .eq("fund_id", fundId)
    .eq("org_id", orgId)
    .single();

  if (sourceError) throw sourceError;
  if (isCashPositionType(sourcePosition?.investment_type)) return null;

  const activityDate =
    toIsoDateOrNull(sourceActivityPayload.activity_date) || todayIso();
  const cashPosition = await getOrCreateCashPosition({
    fundId,
    orgId,
    tenantId,
    userId,
    activityDate,
  });
  const sourceType = trimToEmpty(sourceActivityPayload.activity_type);
  const label =
    sourceType === "interest_income"
      ? "interest income"
      : sourceType === "dividend_income"
      ? "dividend income"
      : sourceType === "redemption" || sourceType === "principal_paydown"
      ? "principal paydown proceeds"
      : "sale proceeds";

  const { error } = await supabase.from("fp_position_activity").insert(
    activityToDbPayload({
      payload: {
        activity_type: "deposit",
        activity_date: activityDate,
        units_delta: amount,
        cash_amount: amount,
        source_type: "system_cash_sweep",
        notes: `Auto-posted cash from ${label} on ${sourcePosition.cusip_or_id || "position"}.`,
      },
      fundId,
      orgId,
      tenantId,
      positionId: cashPosition.id,
      userId,
    })
  );

  if (error) throw error;
  return cashPosition.id;
}

/* ────────────────────────────────
   Provider
──────────────────────────────── */
export function FundsProvider({ children }) {
  const { profile, selectedOrg } = useProfile();

  const suppressDirtyRef = useRef(false);
  const loadedPositionsRef = useRef([]);
  const activeLoadIdRef = useRef(0);

  const [currentId, setCurrentIdState] = useState("");
  const [currentName, setCurrentNameState] = useState("");
  const [description, setDescriptionState] = useState("");
  const [fundType, setFundTypeState] = useState("");
  const [portfolioId, setPortfolioIdState] = useState("");
  const [portfolioIds, setPortfolioIdsState] = useState([]);
  const [sharing, setSharingState] = useState("user");
  const [loadedSharing, setLoadedSharingState] = useState("user");
  const [createdBy, setCreatedByState] = useState(profile?.id || "");
  const currentOwner = useCurrentOwner(createdBy);
  const [yieldMax, setYieldMaxState] = useState("");
  const [sweepPositionId, setSweepPositionIdState] = useState("");
  const [positions, setPositionsState] = useState([makeBlankPosition()]);
  const [portfolios, setPortfolios] = useState([]);

  const [isDirty, setIsDirty] = useState(false);
  const [isLoadingFund, setIsLoadingFund] = useState(false);
  const [isLoadingPortfolios, setIsLoadingPortfolios] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastError, setLastError] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState("");

  const markDirty = useCallback(() => {
    if (!suppressDirtyRef.current) {
      setIsDirty(true);
    }
  }, []);

  const runHydrated = useCallback((fn) => {
    suppressDirtyRef.current = true;
    try {
      fn();
      setIsDirty(false);
    } finally {
      suppressDirtyRef.current = false;
    }
  }, []);

  const setCurrentId = useCallback(
    (value) => {
      setCurrentIdState(value || "");
      markDirty();
    },
    [markDirty]
  );

  const setCurrentName = useCallback(
    (value) => {
      setCurrentNameState(value || "");
      markDirty();
    },
    [markDirty]
  );

  const setDescription = useCallback(
    (value) => {
      setDescriptionState(value || "");
      markDirty();
    },
    [markDirty]
  );

  const setFundType = useCallback(
    (value) => {
      const nextType = value || "";
      setFundTypeState(nextType);
      if (nextType !== "bond_proceeds") {
        setYieldMaxState("");
      }
      markDirty();
    },
    [markDirty]
  );

  const setPortfolioId = useCallback(
    (value) => {
      setPortfolioIdState(value || "");
      setPortfolioIdsState(value ? [value] : []);
      markDirty();
    },
    [markDirty]
  );

  const setPortfolioIds = useCallback(
    (values) => {
      const next = Array.from(new Set((Array.isArray(values) ? values : []).filter(Boolean)));
      setPortfolioIdsState(next);
      setPortfolioIdState(next[0] || "");
      markDirty();
    },
    [markDirty]
  );

  const setYieldMax = useCallback(
    (value) => {
      setYieldMaxState(value ?? "");
      markDirty();
    },
    [markDirty]
  );

  const setSweepPositionId = useCallback(
    (value) => {
      setSweepPositionIdState(value || "");
      markDirty();
    },
    [markDirty]
  );

  const setSharing = useCallback(
    (value) => {
      setSharingState(normalizeSharing(value));
      markDirty();
    },
    [markDirty]
  );

  const setPositions = useCallback(
    (updater) => {
      setPositionsState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        const normalized = Array.isArray(next)
          ? next.map((row) => normalizeUiPosition(row))
          : [];

        return normalized;
      });
      markDirty();
    },
    [markDirty]
  );

  const setPositionsFromCommittedHistory = useCallback((updater) => {
    setPositionsState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return Array.isArray(next)
        ? next.map((row) => normalizeUiPosition(row))
        : [];
    });
  }, []);

  const clearAllState = useCallback(() => {
    loadedPositionsRef.current = [];
    runHydrated(() => {
      setCurrentIdState("");
      setCurrentNameState("");
      setDescriptionState("");
      setFundTypeState("");
      setPortfolioIdState("");
      setPortfolioIdsState([]);
      setSharingState("user");
      setLoadedSharingState("user");
      setCreatedByState(profile?.id || "");
      setYieldMaxState("");
      setSweepPositionIdState("");
      setPositionsState([makeBlankPosition()]);
      setLastError("");
      setLastSavedAt("");
    });
  }, [profile?.id, runHydrated]);

  const permissions = getSharingPermissions(
    { id: currentId, created_by: createdBy, sharing },
    profile
  );
  const {
    isOwner,
    canEdit,
    canDelete,
    canChangeSharing,
    isReadOnly,
    isLocked,
  } = permissions;

  const refreshPortfolios = useCallback(async () => {
    if (!selectedOrg?.id) {
      setPortfolios([]);
      return [];
    }

    setIsLoadingPortfolios(true);

    const { data, error } = await supabase
      .from("fp_portfolios")
      .select("id, name")
      .eq("org_id", selectedOrg.id)
      .order("name", { ascending: true });

    setIsLoadingPortfolios(false);

    if (error) {
      console.error("Error loading portfolios:", error);
      setPortfolios([]);
      return [];
    }

    const next = Array.isArray(data) ? data : [];
    setPortfolios(next);
    return next;
  }, [selectedOrg?.id]);

  const createPortfolioGroup = useCallback(
    async (name) => {
      if (!selectedOrg?.id || !profile?.id || !profile?.tenant_id) {
        const msg = "Missing org or user context.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      const trimmedName = trimToEmpty(name);
      if (!trimmedName) {
        const msg = "Portfolio Group name is required.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      setLastError("");

      try {
        const { data, error } = await supabase
          .from("fp_portfolios")
          .insert({
            org_id: selectedOrg.id,
            tenant_id: profile.tenant_id,
            created_by: profile.id,
            name: trimmedName,
          })
          .select("id, name")
          .single();

        if (error) throw error;

        setPortfolios((prev) => {
          const merged = [...(Array.isArray(prev) ? prev : []), data];
          merged.sort((a, b) =>
            String(a?.name || "").localeCompare(String(b?.name || ""))
          );
          return merged;
        });

        return { ok: true, data };
      } catch (error) {
        console.error("Error creating Portfolio Group:", error);
        const msg = error?.message || "Unable to create Portfolio Group.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [profile?.id, profile?.tenant_id, selectedOrg?.id]
  );

  const resetFund = useCallback(() => {
    clearAllState();
  }, [clearAllState]);

  const loadFund = useCallback(
    async (fundId) => {
      if (!fundId) {
        clearAllState();
        return { ok: true };
      }

      if (!selectedOrg?.id) {
        const msg = "Select an organization before opening a fund.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      const loadId = Date.now();
      activeLoadIdRef.current = loadId;

      setIsLoadingFund(true);
      setLastError("");

      const [fundRes, membershipRes, posRes, valuationRes, activityRes] = await Promise.all([
        supabase
          .from("fp_investment_funds")
          .select(
            `
            id,
            name,
            description,
            fund_type,
            portfolio_id,
            yield_max,
            sweep_position_id,
            created_by,
            sharing
          `
          )
          .eq("id", fundId)
          .eq("org_id", selectedOrg.id)
          .maybeSingle(),
        supabase
          .from("fp_investment_fund_portfolios")
          .select("portfolio_id")
          .eq("fund_id", fundId)
          .order("created_at", { ascending: true }),
        supabase
          .from("fp_positions")
          .select("*")
          .eq("fund_id", fundId)
          .eq("org_id", selectedOrg.id)
          .order("settlement_date", { ascending: true })
          .order("created_at", { ascending: true }),
        supabase
          .from("fp_position_valuations")
          .select("*")
          .eq("fund_id", fundId)
          .eq("org_id", selectedOrg.id)
          .order("valuation_date", { ascending: false })
          .order("created_at", { ascending: false }),
        supabase
          .from("fp_position_activity")
          .select("*")
          .eq("fund_id", fundId)
          .eq("org_id", selectedOrg.id)
          .order("activity_date", { ascending: false })
          .order("created_at", { ascending: false }),
      ]);

      if (activeLoadIdRef.current !== loadId) {
        setIsLoadingFund(false);
        return { ok: false, error: "Stale load ignored." };
      }

      setIsLoadingFund(false);

      if (fundRes.error) {
        console.error("Error loading fund:", fundRes.error);
        const msg = fundRes.error.message || "Unable to load fund.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (posRes.error) {
        console.error("Error loading positions:", posRes.error);
        const msg = posRes.error.message || "Unable to load positions.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (membershipRes.error) {
        console.error("Error loading Portfolio Group memberships:", membershipRes.error);
        const msg = membershipRes.error.message || "Unable to load Portfolio Group memberships.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (valuationRes.error) {
        console.error("Error loading position valuations:", valuationRes.error);
        const msg =
          valuationRes.error.message || "Unable to load position valuations.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (activityRes.error) {
        console.error("Error loading position activity:", activityRes.error);
        const msg =
          activityRes.error.message || "Unable to load position activity.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      const fund = fundRes.data;
      if (!fund) {
        const msg = "Fund not found.";
        loadedPositionsRef.current = [];
        clearAllState();
        setLastError(msg);
        return { ok: false, error: msg };
      }

      const rawPositions = Array.isArray(posRes.data) ? posRes.data : [];
      const valuationRows = Array.isArray(valuationRes.data)
        ? valuationRes.data
        : [];
      const latestValuationByPosition = buildLatestByKey(
        valuationRows,
        "position_id"
      );
      const initialValuationByPosition = buildLatestByKey(
        valuationRows.filter((row) => row?.source_type === "initial_position"),
        "position_id"
      );
      const positionById = new Map(
        rawPositions.map((position) => [position.id, position])
      );
      const historyByPosition = new Map();

      valuationRows.forEach((row) => {
        const rows = historyByPosition.get(row.position_id) || [];
        rows.push({
          ...row,
          history_type: "valuation",
          history_date: row.valuation_date,
        });
        historyByPosition.set(row.position_id, rows);
      });

      (Array.isArray(activityRes.data) ? activityRes.data : []).forEach((row) => {
        const rows = historyByPosition.get(row.position_id) || [];
        rows.push({
          ...enrichScheduledCashflowRow(row, positionById),
          history_type: "activity",
          history_date: row.activity_date,
        });
        historyByPosition.set(row.position_id, rows);
      });

      const cusips = Array.from(
        new Set(
          rawPositions.map((row) => normalizeCusip(row?.cusip_or_id)).filter(Boolean)
        )
      );
      let securityByCusip = new Map();
      let latestSecurityValuationBySecurityId = new Map();

      if (cusips.length) {
        const securityRes = await supabase
          .from("fp_security_master")
          .select("id,cusip,maturity_date")
          .in("cusip", cusips);

        if (securityRes.error) {
          console.error("Error loading security master:", securityRes.error);
          const msg =
            securityRes.error.message || "Unable to load security master.";
          setLastError(msg);
          return { ok: false, error: msg };
        }

        const securities = Array.isArray(securityRes.data) ? securityRes.data : [];
        securityByCusip = new Map(
          securities.map((row) => [normalizeCusip(row.cusip), row])
        );

        const securityIds = securities.map((row) => row.id).filter(Boolean);
        if (securityIds.length) {
          const securityValuationRes = await supabase
            .from("fp_security_master_valuations")
            .select("security_master_id,valuation_date,price_mid,created_at")
            .in("security_master_id", securityIds)
            .order("valuation_date", { ascending: false })
            .order("created_at", { ascending: false });

          if (securityValuationRes.error) {
            console.error(
              "Error loading security master valuations:",
              securityValuationRes.error
            );
            const msg =
              securityValuationRes.error.message ||
              "Unable to load security master valuations.";
            setLastError(msg);
            return { ok: false, error: msg };
          }

          latestSecurityValuationBySecurityId = buildLatestByKey(
            securityValuationRes.data,
            "security_master_id"
          );
        }
      }

      const dbPositions = rawPositions.map((row) => {
        const latestPositionValuation =
          latestValuationByPosition.get(row.id) || null;
        const initialPositionValuation =
          initialValuationByPosition.get(row.id) || null;
        const security = securityByCusip.get(normalizeCusip(row.cusip_or_id)) || null;
        const latestSecurityValuation = security
          ? latestSecurityValuationBySecurityId.get(security.id) || null
          : null;
        const derivedAmounts = derivePositionAmountsFromHistory(
          row,
          historyByPosition.get(row.id) || []
        );
        const saleDate = latestSaleDate(historyByPosition.get(row.id) || []);
        const shouldClose = saleDate && isZeroBalance(row, derivedAmounts);

        return {
          ...row,
          latest_valuation: latestPositionValuation,
          initial_rating_rollup:
            initialPositionValuation?.rating_rollup || "",
          latest_rating_valuation: latestRatingValuation(
            historyByPosition.get(row.id) || []
          ),
          latest_security_master_valuation: latestSecurityValuation,
          latest_priced_valuation: latestPricedValuation(
            latestPositionValuation,
            latestSecurityValuation
          ),
          security_maturity_date: security?.maturity_date || "",
          current_face: derivedAmounts.currentFace ?? "",
          units: derivedAmounts.units ?? "",
          balance_amount: derivedAmounts.balanceAmount ?? "",
          closed_date: shouldClose
            ? saleDate
            : saleDate
            ? null
            : row.closed_date,
        };
      });
      loadedPositionsRef.current = dbPositions;

      runHydrated(() => {
        setCurrentIdState(fund.id || "");
        setCurrentNameState(fund.name || "");
        setDescriptionState(fund.description || "");
        setFundTypeState(trimToEmpty(fund.fund_type));
        const loadedPortfolioIds = (membershipRes.data || [])
          .map((row) => row.portfolio_id)
          .filter(Boolean);
        const effectivePortfolioIds = loadedPortfolioIds.length
          ? loadedPortfolioIds
          : fund.portfolio_id
          ? [fund.portfolio_id]
          : [];
        setPortfolioIdsState(effectivePortfolioIds);
        setPortfolioIdState(effectivePortfolioIds[0] || "");
        setSharingState(normalizeSharing(fund.sharing));
        setLoadedSharingState(normalizeSharing(fund.sharing));
        setCreatedByState(fund.created_by || "");
        setYieldMaxState(fund.yield_max ?? "");
        setSweepPositionIdState(fund.sweep_position_id || "");
        setPositionsState([
          ...dbPositions.map((row) =>
            normalizeUiPosition(buildPositionFromDb(row))
          ),
          makeBlankPosition(),
        ]);
        setLastError("");
        setLastSavedAt(new Date().toISOString());
      });

      return { ok: true, id: fund.id || "" };
    },
    [clearAllState, runHydrated, selectedOrg?.id]
  );

  const saveAsFund = useCallback(
    ({ name } = {}) => {
      const nextName =
        trimToEmpty(name) || `${trimToEmpty(currentName) || "New Fund"} Copy`;

      loadedPositionsRef.current = [];

      setCurrentIdState("");
      setCreatedByState(profile?.id || "");
      setSharingState("user");
      setLoadedSharingState("user");
      setSweepPositionIdState("");

      setPositionsState((prev) => {
        const sourceRows =
          Array.isArray(prev) && prev.length ? prev : [makeBlankPosition()];

        return sourceRows.map((row) => ({
          ...normalizeUiPosition(row),
          id: makeTempId(),
          __isNew: true,
        }));
      });

      setCurrentNameState(nextName);
      setLastError("");
      setLastSavedAt("");
      setIsDirty(true);

      return { ok: true };
    },
    [currentName, profile?.id]
  );

  const deleteFund = useCallback(async () => {
    if (!currentId) {
      clearAllState();
      return { ok: true };
    }

    if (!canDelete) {
      const msg = "You do not have permission to delete this fund.";
      setLastError(msg);
      return { ok: false, error: msg };
    }

    if (!selectedOrg?.id || !profile?.id) {
      const msg = "Missing org or user context.";
      setLastError(msg);
      return { ok: false, error: msg };
    }

    setIsSaving(true);
    setLastError("");

    try {
      const { error } = await supabase
        .from("fp_investment_funds")
        .delete()
        .eq("id", currentId)
        .eq("org_id", selectedOrg.id);

      if (error) throw error;

      clearAllState();
      setIsSaving(false);
      return { ok: true };
    } catch (error) {
      console.error("Error deleting fund:", error);
      const msg = error?.message || "Unable to delete fund.";
      setLastError(msg);
      setIsSaving(false);
      return { ok: false, error: msg };
    }
  }, [
    clearAllState,
    canDelete,
    currentId,
    profile?.id,
    selectedOrg?.id,
  ]);

  const saveFund = useCallback(async () => {
    if (!selectedOrg?.id || !profile?.id || !profile?.tenant_id) {
      const msg = "Missing org or user context.";
      setLastError(msg);
      return { ok: false, error: msg };
    }

    if (currentId && !canEdit) {
      const msg = "You do not have permission to save this fund.";
      setLastError(msg);
      return { ok: false, error: msg };
    }

    const trimmedName = trimToEmpty(currentName);
    if (!trimmedName) {
      const msg = "Fund name is required.";
      setLastError(msg);
      return { ok: false, error: msg };
    }

    const normalizedPortfolioIds = Array.from(
      new Set((portfolioIds || []).filter((id) => id && id !== "__new__"))
    );
    const normalizedPortfolioId = normalizedPortfolioIds[0] || null;

    const normalizedYieldMax =
      trimToEmpty(fundType) === "bond_proceeds"
        ? toNumberOrNull(yieldMax)
        : null;

    const normalizedSharing =
      !currentId || canChangeSharing
        ? normalizeSharing(sharing)
        : normalizeSharing(loadedSharing);

    const rowsForValidation = (positions || [])
      .map((row) => normalizeUiPosition(row))
      .filter((row) => !isBlankPosition(row));

    const openCashRows = rowsForValidation.filter(
      (row) => isCashPositionType(row.investment_type) && !toIsoDateOrNull(row.closed_date)
    );
    if (openCashRows.length > 1) {
      const message = "Each fund can only have one open Cash position.";
      setLastError(message);
      return { ok: false, error: message };
    }

    for (let i = 0; i < rowsForValidation.length; i += 1) {
      const message = validatePosition(rowsForValidation[i], i + 1);
      if (message) {
        setLastError(message);
        return { ok: false, error: message };
      }
    }

    setIsSaving(true);
    setLastError("");

    try {
      let fundId = currentId;

      if (!fundId) {
        const { data, error } = await supabase
          .from("fp_investment_funds")
          .insert({
            org_id: selectedOrg.id,
            tenant_id: profile.tenant_id,
            created_by: profile.id,
            name: trimmedName,
            description: trimToNull(description),
            fund_type: trimToEmpty(fundType) || "bond_proceeds",
            portfolio_id: normalizedPortfolioId,
            yield_max: normalizedYieldMax,
            sweep_position_id: sweepPositionId || null,
            sharing: normalizedSharing,
          })
          .select("id, name")
          .single();

        if (error) throw error;

        fundId = data.id;

        runHydrated(() => {
          setCurrentIdState(fundId);
          setCurrentNameState(data.name || trimmedName);
          setSharingState(normalizedSharing);
          setLoadedSharingState(normalizedSharing);
          setCreatedByState(profile.id);
        });
      } else {
        const { error } = await supabase
          .from("fp_investment_funds")
          .update({
            name: trimmedName,
            description: trimToNull(description),
            fund_type: trimToEmpty(fundType) || "bond_proceeds",
            portfolio_id: normalizedPortfolioId,
            yield_max: normalizedYieldMax,
            sweep_position_id: sweepPositionId || null,
            sharing: normalizedSharing,
          })
          .eq("id", fundId)
          .eq("org_id", selectedOrg.id);

        if (error) throw error;

      }

      const { error: membershipError } = await supabase.rpc(
        "fp_set_investment_fund_portfolios",
        { p_fund_id: fundId, p_portfolio_ids: normalizedPortfolioIds }
      );
      if (membershipError) throw membershipError;

      const originalById = new Map(
        (loadedPositionsRef.current || []).map((row) => [row.id, row])
      );

      const rowsToSave = rowsForValidation.map((row) => normalizeUiPosition(row));
      const activeSavedIds = new Set(
        rowsToSave.filter((row) => isUuid(row.id)).map((row) => row.id)
      );

      const rowsToInsert = rowsToSave.filter((row) => !isUuid(row.id));
      const rowsToUpdate = rowsToSave.filter((row) => {
        if (!isUuid(row.id)) return false;
        return hasPositionChanged(row, originalById.get(row.id));
      });
      const rowsToDelete = (loadedPositionsRef.current || []).filter(
        (row) => !activeSavedIds.has(row.id)
      );
      const slgsInitialRecalcRequests = [];
      const savedRowsWithIds = [...rowsToUpdate];

      if (rowsToInsert.length) {
        const insertPayload = rowsToInsert.map((row) =>
          positionToDbPayload({
            row,
            fundId,
            orgId: selectedOrg.id,
            tenantId: profile.tenant_id,
            userId: profile.id,
          })
        );

        const { data: insertedPositions, error } = await supabase
          .from("fp_positions")
          .insert(insertPayload)
          .select("*");
        if (error) throw error;

        const initialValuationPayload = (Array.isArray(insertedPositions)
          ? insertedPositions
          : []
        ).map((position, index) =>
          initialPositionValuationToDbPayload({
            row: rowsToInsert[index] || {},
            positionId: position.id,
            fundId,
            orgId: selectedOrg.id,
            tenantId: profile.tenant_id,
            userId: profile.id,
          })
        );

        if (initialValuationPayload.length) {
          const { error: valuationError } = await supabase
            .from("fp_position_valuations")
            .insert(initialValuationPayload);
          if (valuationError) throw valuationError;
        }

        (Array.isArray(insertedPositions) ? insertedPositions : []).forEach(
          (position, index) => {
            savedRowsWithIds.push({
              ...(rowsToInsert[index] || {}),
              id: position.id,
            });
          }
        );
      }

      for (const row of rowsToUpdate) {
        const preserve = originalById.get(row.id) || {};
        const previousInitialDate =
          toIsoDateOrNull(preserve.settlement_date) ||
          toIsoDateOrNull(preserve.purchase_date) ||
          "";
        const nextInitialDate =
          toIsoDateOrNull(row.settlement_date) ||
          toIsoDateOrNull(row.purchase_date) ||
          "";
        const previousInitialAmount = toNumberOrNull(preserve.par_amount);
        const nextInitialAmount = toNumberOrNull(row.par_amount);

        if (
          isSlgsDemandDepositType(row.investment_type) &&
          (previousInitialDate !== nextInitialDate ||
            previousInitialAmount !== nextInitialAmount)
        ) {
          const recalcFromDate =
            [previousInitialDate, nextInitialDate].filter(Boolean).sort()[0] ||
            nextInitialDate ||
            previousInitialDate;

          if (recalcFromDate) {
            slgsInitialRecalcRequests.push({
              positionId: row.id,
              fromDate: recalcFromDate,
            });
          }
        }

        const { error } = await supabase
          .from("fp_positions")
          .update(
            positionToDbPayload({
              row,
              fundId,
              orgId: selectedOrg.id,
              tenantId: profile.tenant_id,
              userId: profile.id,
              preserve,
            })
          )
          .eq("id", row.id)
          .eq("fund_id", fundId)
          .eq("org_id", selectedOrg.id);

        if (error) throw error;

        await syncInitialPositionValuation({
          row,
          originalRow: preserve,
          positionId: row.id,
          fundId,
          orgId: selectedOrg.id,
          tenantId: profile.tenant_id,
          userId: profile.id,
        });
      }

      for (const request of slgsInitialRecalcRequests) {
        const { data: recalcRangeData, error: recalcRangeError } =
          await supabase.rpc("fp_slgs_demand_accrual_recalc_range", {
            p_position_id: request.positionId,
            p_from_date: request.fromDate,
          });

        if (recalcRangeError) throw recalcRangeError;

        const recalcRange = Array.isArray(recalcRangeData)
          ? recalcRangeData[0]
          : null;

        if (!recalcRange?.end_date) continue;

        const { error: recalcError } = await supabase.rpc(
          "fp_recalculate_slgs_demand_accruals",
          {
            p_position_id: request.positionId,
            p_from_date: recalcRange.start_date,
            p_end_date: recalcRange.end_date,
          }
        );

        if (recalcError) throw recalcError;
      }

      for (const row of savedRowsWithIds) {
        await syncPositionPurchaseFunding({
          row,
          orgId: selectedOrg.id,
          tenantId: profile.tenant_id,
          userId: profile.id,
          profile,
        });
      }

      if (rowsToDelete.length) {
        const idsToDelete = rowsToDelete.map((row) => row.id);

        const { error } = await supabase
          .from("fp_positions")
          .delete()
          .in("id", idsToDelete)
          .eq("fund_id", fundId)
          .eq("org_id", selectedOrg.id);

        if (error) throw error;
      }

      const reload = await loadFund(fundId);
      if (!reload.ok) {
        throw new Error(reload.error || "Saved, but failed to reload fund.");
      }

      setIsSaving(false);
      return { ok: true, id: fundId };
    } catch (error) {
      console.error("Error saving fund:", error);
      const msg = error?.message || "Unable to save fund.";
      setLastError(msg);
      setIsSaving(false);
      return { ok: false, error: msg };
    }
  }, [
    currentId,
    currentName,
    canChangeSharing,
    canEdit,
    description,
    fundType,
    loadedSharing,
    loadFund,
    portfolioIds,
    positions,
    profile,
    runHydrated,
    selectedOrg?.id,
    sharing,
    sweepPositionId,
    yieldMax,
  ]);

  const loadTransferDestinationPositions = useCallback(
    async ({ sourcePositionId } = {}) => {
      if (!selectedOrg?.id || !profile?.id) {
        const msg = "Missing org or user context.";
        setLastError(msg);
        return { ok: false, error: msg, data: [] };
      }

      setLastError("");

      try {
        const { data: fundsData, error: fundsError } = await supabase
          .from("fp_investment_funds")
          .select("id,name,created_by,sharing")
          .eq("org_id", selectedOrg.id);

        if (fundsError) throw fundsError;

        const editableFunds = (Array.isArray(fundsData) ? fundsData : [])
          .map((fund) => ({
            ...fund,
            ...getSharingPermissions(fund, profile),
          }))
          .filter((fund) => fund.canEdit);
        const fundIds = editableFunds.map((fund) => fund.id).filter(Boolean);

        if (!fundIds.length) return { ok: true, data: [] };

        const { data: positionsData, error: positionsError } = await supabase
          .from("fp_positions")
          .select(
            "id,fund_id,cusip_or_id,investment_type,raw_security_description,raw_issuer_name,closed_date,is_active"
          )
          .eq("org_id", selectedOrg.id)
          .in("fund_id", fundIds)
          .is("closed_date", null)
          .order("investment_type", { ascending: true })
          .order("cusip_or_id", { ascending: true });

        if (positionsError) throw positionsError;

        const fundById = new Map(editableFunds.map((fund) => [fund.id, fund]));
        const rows = (Array.isArray(positionsData) ? positionsData : [])
          .filter((position) => position.id !== sourcePositionId)
          .filter((position) =>
            isTransferEligiblePositionType(position.investment_type)
          )
          .filter((position) => position.is_active !== false)
          .map((position) => {
            const fund = fundById.get(position.fund_id) || {};
            const labelParts = [
              fund.name,
              position.cusip_or_id || "position",
            ].filter(Boolean);

            return {
              id: position.id,
              position_id: position.id,
              fund_id: position.fund_id,
              fund_name: fund.name || "",
              cusip_or_id: position.cusip_or_id || "",
              investment_type: position.investment_type || "",
              description: position.raw_security_description || "",
              issuer: position.raw_issuer_name || "",
              label: labelParts.join(" / "),
            };
          });

        return { ok: true, data: rows };
      } catch (error) {
        console.error("Error loading transfer destinations:", error);
        const msg = error?.message || "Unable to load transfer destinations.";
        setLastError(msg);
        return { ok: false, error: msg, data: [] };
      }
    },
    [profile, selectedOrg?.id]
  );

  const savePositionActivity = useCallback(
    async (positionId, payload = {}) => {
      const contextError = ensureActivityValuationContext({
        positionId,
        currentId,
        selectedOrg,
        profile,
      });

      if (contextError) {
        setLastError(contextError);
        return { ok: false, error: contextError };
      }

      setLastError("");

      try {
        const activityPayloads = Array.isArray(payload.activities)
          ? payload.activities
          : [payload];
        const dbPayloads = activityPayloads.map((activityPayload) =>
          activityToDbPayload({
            payload: activityPayload,
            fundId: currentId,
            orgId: selectedOrg.id,
            tenantId: profile.tenant_id,
            positionId,
            userId: profile.id,
          })
        );

        const { error } = await supabase
          .from("fp_position_activity")
          .insert(dbPayloads);

        if (error) throw error;

        for (const activityPayload of activityPayloads) {
          await postCashOffsetActivity({
            sourcePositionId: positionId,
            sourceActivityPayload: activityPayload,
            fundId: currentId,
            orgId: selectedOrg.id,
            tenantId: profile.tenant_id,
            userId: profile.id,
          });
        }

        return { ok: true };
      } catch (error) {
        console.error("Error saving position activity:", error);
        const msg = error?.message || "Unable to save position activity.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [currentId, profile, selectedOrg]
  );

  const saveManualTransfer = useCallback(
    async (sourcePositionId, payload = {}) => {
      const contextError = ensureActivityValuationContext({
        positionId: sourcePositionId,
        currentId,
        selectedOrg,
        profile,
      });

      if (contextError) {
        setLastError(contextError);
        return { ok: false, error: contextError };
      }

      const destinationPositionId = trimToEmpty(payload.destination_position_id);
      const amount = Math.abs(toNumberOrNull(payload.amount) ?? 0);

      if (!isUuid(destinationPositionId)) {
        const msg = "Select a destination position for the transfer.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (destinationPositionId === sourcePositionId) {
        const msg = "Select a destination position other than the source position.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (amount <= 0) {
        const msg = "Enter a transfer amount greater than zero.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      setLastError("");

      try {
        const { data: positionRows, error: positionError } = await supabase
          .from("fp_positions")
          .select("id,fund_id,investment_type,cusip_or_id")
          .eq("org_id", selectedOrg.id)
          .in("id", [sourcePositionId, destinationPositionId]);

        if (positionError) throw positionError;

        const sourcePosition = (Array.isArray(positionRows) ? positionRows : []).find(
          (row) => row.id === sourcePositionId
        );
        const destinationPosition = (Array.isArray(positionRows)
          ? positionRows
          : []
        ).find((row) => row.id === destinationPositionId);

        if (!sourcePosition || !destinationPosition) {
          throw new Error("Source or destination position was not found.");
        }

        if (
          !isTransferEligiblePositionType(sourcePosition.investment_type) ||
          !isTransferEligiblePositionType(destinationPosition.investment_type)
        ) {
          throw new Error(
            "Transfers are only available for Cash, LGIP, Money Market Funds, and Bank Deposit positions."
          );
        }

        const fundIds = Array.from(
          new Set([sourcePosition.fund_id, destinationPosition.fund_id].filter(Boolean))
        );
        const { data: fundRows, error: fundError } = await supabase
          .from("fp_investment_funds")
          .select("id,created_by,sharing")
          .eq("org_id", selectedOrg.id)
          .in("id", fundIds);

        if (fundError) throw fundError;

        const editableFundIds = new Set(
          (Array.isArray(fundRows) ? fundRows : [])
            .map((fund) => ({
              ...fund,
              ...getSharingPermissions(fund, profile),
            }))
            .filter((fund) => fund.canEdit)
            .map((fund) => fund.id)
        );

        if (!fundIds.every((fundId) => editableFundIds.has(fundId))) {
          throw new Error("You must be able to edit both funds to record a transfer.");
        }

        const activityDate = toIsoDateOrNull(payload.activity_date) || todayIso();
        const groupId = linkedActivityGroupId(payload.external_activity_id) || randomUuid();
        const notes = trimToEmpty(payload.notes);
        const dbPayloads = [
          activityToDbPayload({
            payload: {
              activity_type: "transfer_out",
              activity_date: activityDate,
              units_delta: -amount,
              cash_amount: -amount,
              source_type: "manual_transfer",
              external_activity_id: `${groupId}:out`,
              notes,
            },
            fundId: sourcePosition.fund_id,
            orgId: selectedOrg.id,
            tenantId: profile.tenant_id,
            positionId: sourcePosition.id,
            userId: profile.id,
          }),
          activityToDbPayload({
            payload: {
              activity_type: "transfer_in",
              activity_date: activityDate,
              units_delta: amount,
              cash_amount: amount,
              source_type: "manual_transfer",
              external_activity_id: `${groupId}:in`,
              notes,
            },
            fundId: destinationPosition.fund_id,
            orgId: selectedOrg.id,
            tenantId: profile.tenant_id,
            positionId: destinationPosition.id,
            userId: profile.id,
          }),
        ];

        const { error } = await supabase
          .from("fp_position_activity")
          .insert(dbPayloads);

        if (error) throw error;

        return {
          ok: true,
          affectedPositionIds: [sourcePosition.id, destinationPosition.id],
          affectedFundIds: [sourcePosition.fund_id, destinationPosition.fund_id],
        };
      } catch (error) {
        console.error("Error saving transfer:", error);
        const msg = error?.message || "Unable to save transfer.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [currentId, profile, selectedOrg]
  );

  const savePositionValuation = useCallback(
    async (positionId, payload = {}) => {
      const contextError = ensureActivityValuationContext({
        positionId,
        currentId,
        selectedOrg,
        profile,
      });

      if (contextError) {
        setLastError(contextError);
        return { ok: false, error: contextError };
      }

      setLastError("");

      try {
        const { error } = await supabase.from("fp_position_valuations").insert(
          valuationToDbPayload({
            payload,
            fundId: currentId,
            orgId: selectedOrg.id,
            tenantId: profile.tenant_id,
            positionId,
            userId: profile.id,
          })
        );

        if (error) throw error;

        return { ok: true };
      } catch (error) {
        console.error("Error saving position valuation:", error);
        const msg = isMissingPositionValuationRatingColumnError(error)
          ? "Unable to save Credit Rating until the fp_position_valuations.rating_rollup database column is added."
          : error?.message || "Unable to save position valuation.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [currentId, profile, selectedOrg]
  );

  const updatePositionActivity = useCallback(
    async (activityId, payload = {}) => {
      if (!selectedOrg?.id || !profile?.id || !currentId) {
        const msg = "Missing org, user, or fund context.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (!isUuid(activityId)) {
        const msg = "Missing activity row id.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      setLastError("");

      try {
        const { data: managedActivity, error: managedActivityError } =
          await supabase
            .from("fp_position_activity")
            .select("source_type")
            .eq("id", activityId)
            .eq("org_id", selectedOrg.id)
            .single();
        if (managedActivityError) throw managedActivityError;
        if (managedActivity?.source_type === "position_purchase_funding") {
          throw new Error(
            "Purchase Funding activity is managed by its funded position. Change or clear Source of Funds on that position instead."
          );
        }

        if (Array.isArray(payload.activities)) {
          const { data: existingActivity, error: existingError } = await supabase
            .from("fp_position_activity")
            .select("*")
            .eq("id", activityId)
            .eq("fund_id", currentId)
            .eq("org_id", selectedOrg.id)
            .single();

          if (existingError) throw existingError;

          const replaceExternalActivityId = reinvestmentGroupId(
            payload.replace_external_activity_id ||
              existingActivity?.external_activity_id
          );

          let deleteQuery = supabase
            .from("fp_position_activity")
            .delete()
            .eq("fund_id", currentId)
            .eq("org_id", selectedOrg.id);

          if (replaceExternalActivityId) {
            deleteQuery = deleteQuery.like(
              "external_activity_id",
              `${replaceExternalActivityId}:%`
            );
          } else {
            deleteQuery = deleteQuery.eq("id", activityId);
          }

          const { error: deleteError } = await deleteQuery;
          if (deleteError) throw deleteError;

          const dbPayloads = payload.activities.map((activityPayload) =>
            activityToDbPayload({
              payload: activityPayload,
              fundId: currentId,
              orgId: selectedOrg.id,
              tenantId: profile.tenant_id,
              positionId: existingActivity.position_id,
              userId: profile.id,
            })
          );
          const { error: insertError } = await supabase
            .from("fp_position_activity")
            .insert(dbPayloads);

          if (insertError) throw insertError;

          for (const activityPayload of payload.activities) {
            await postCashOffsetActivity({
              sourcePositionId: existingActivity.position_id,
              sourceActivityPayload: activityPayload,
              fundId: currentId,
              orgId: selectedOrg.id,
              tenantId: profile.tenant_id,
              userId: profile.id,
            });
          }

          return { ok: true };
        }

        if (payload.replace_external_activity_id) {
          const { data: existingActivity, error: existingError } = await supabase
            .from("fp_position_activity")
            .select("*")
            .eq("id", activityId)
            .eq("fund_id", currentId)
            .eq("org_id", selectedOrg.id)
            .single();

          if (existingError) throw existingError;

          const replaceExternalActivityId = reinvestmentGroupId(
            payload.replace_external_activity_id ||
              existingActivity?.external_activity_id
          );

          let deleteQuery = supabase
            .from("fp_position_activity")
            .delete()
            .eq("fund_id", currentId)
            .eq("org_id", selectedOrg.id);

          if (replaceExternalActivityId) {
            deleteQuery = deleteQuery.like(
              "external_activity_id",
              `${replaceExternalActivityId}:%`
            );
          } else {
            deleteQuery = deleteQuery.eq("id", activityId);
          }

          const { error: deleteError } = await deleteQuery;
          if (deleteError) throw deleteError;

          const { error: insertError } = await supabase
            .from("fp_position_activity")
            .insert(
              activityToDbPayload({
                payload,
                fundId: currentId,
                orgId: selectedOrg.id,
                tenantId: profile.tenant_id,
                positionId: existingActivity.position_id,
                userId: profile.id,
              })
            );

          if (insertError) throw insertError;

          await postCashOffsetActivity({
            sourcePositionId: existingActivity.position_id,
            sourceActivityPayload: payload,
            fundId: currentId,
            orgId: selectedOrg.id,
            tenantId: profile.tenant_id,
            userId: profile.id,
          });

          return { ok: true };
        }

        const { error } = await supabase
          .from("fp_position_activity")
          .update(activityUpdatePayload(payload))
          .eq("id", activityId)
          .eq("fund_id", currentId)
          .eq("org_id", selectedOrg.id);

        if (error) throw error;

        return { ok: true };
      } catch (error) {
        console.error("Error updating position activity:", error);
        const msg = error?.message || "Unable to update position activity.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [currentId, profile?.id, profile?.tenant_id, selectedOrg?.id]
  );

  const updateManualTransfer = useCallback(
    async (activityId, payload = {}) => {
      if (!selectedOrg?.id || !profile?.id || !currentId) {
        const msg = "Missing org, user, or fund context.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (!isUuid(activityId)) {
        const msg = "Missing activity row id.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      setLastError("");

      try {
        const { data: existingActivity, error: existingError } = await supabase
          .from("fp_position_activity")
          .select("*")
          .eq("id", activityId)
          .eq("org_id", selectedOrg.id)
          .single();

        if (existingError) throw existingError;

        const replaceGroupId = linkedActivityGroupId(
          payload.replace_external_activity_id ||
            existingActivity?.external_activity_id
        );

        if (!replaceGroupId) {
          throw new Error("Missing linked transfer id.");
        }

        const { error: deleteError } = await supabase
          .from("fp_position_activity")
          .delete()
          .eq("org_id", selectedOrg.id)
          .eq("source_type", "manual_transfer")
          .like("external_activity_id", `${replaceGroupId}:%`);

        if (deleteError) throw deleteError;

        return await saveManualTransfer(payload.source_position_id, {
          ...payload,
          external_activity_id: replaceGroupId,
        });
      } catch (error) {
        console.error("Error updating transfer:", error);
        const msg = error?.message || "Unable to update transfer.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [currentId, profile?.id, saveManualTransfer, selectedOrg?.id]
  );

  const updatePositionValuation = useCallback(
    async (valuationId, payload = {}) => {
      if (!selectedOrg?.id || !profile?.id || !currentId) {
        const msg = "Missing org, user, or fund context.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (!isUuid(valuationId)) {
        const msg = "Missing valuation row id.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      setLastError("");

      try {
        const { error } = await supabase
          .from("fp_position_valuations")
          .update(valuationUpdatePayload(payload))
          .eq("id", valuationId)
          .eq("fund_id", currentId)
          .eq("org_id", selectedOrg.id);

        if (error) throw error;

        return { ok: true };
      } catch (error) {
        console.error("Error updating position valuation:", error);
        const msg = isMissingPositionValuationRatingColumnError(error)
          ? "Unable to save Credit Rating until the fp_position_valuations.rating_rollup database column is added."
          : error?.message || "Unable to update position valuation.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [currentId, profile?.id, selectedOrg?.id]
  );

  const deletePositionActivity = useCallback(
    async (activityId) => {
      if (!selectedOrg?.id || !profile?.id || !currentId) {
        const msg = "Missing org, user, or fund context.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (!isUuid(activityId)) {
        const msg = "Missing activity row id.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      setLastError("");

      try {
        const { data: existingActivity, error: existingError } = await supabase
          .from("fp_position_activity")
          .select("id,source_type,external_activity_id")
          .eq("id", activityId)
          .eq("org_id", selectedOrg.id)
          .single();

        if (existingError) throw existingError;

        if (existingActivity?.source_type === "position_purchase_funding") {
          throw new Error(
            "Purchase Funding activity is managed by its funded position. Change or clear Source of Funds on that position instead."
          );
        }

        let deleteQuery = supabase
          .from("fp_position_activity")
          .delete()
          .eq("org_id", selectedOrg.id);

        if (
          existingActivity?.source_type === "manual_interest_reinvestment" &&
          existingActivity?.external_activity_id
        ) {
          deleteQuery = deleteQuery.like(
            "external_activity_id",
            `${reinvestmentGroupId(existingActivity.external_activity_id)}:%`
          ).eq("fund_id", currentId);
        } else if (
          existingActivity?.source_type === "manual_transfer" &&
          existingActivity?.external_activity_id
        ) {
          deleteQuery = deleteQuery
            .eq("source_type", "manual_transfer")
            .like(
              "external_activity_id",
              `${linkedActivityGroupId(existingActivity.external_activity_id)}:%`
            );
        } else {
          deleteQuery = deleteQuery.eq("id", activityId).eq("fund_id", currentId);
        }

        const { error } = await deleteQuery;

        if (error) throw error;

        return { ok: true };
      } catch (error) {
        console.error("Error deleting position activity:", error);
        const msg = error?.message || "Unable to delete position activity.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [currentId, profile?.id, selectedOrg?.id]
  );

  const deletePositionValuation = useCallback(
    async (valuationId) => {
      if (!selectedOrg?.id || !profile?.id || !currentId) {
        const msg = "Missing org, user, or fund context.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      if (!isUuid(valuationId)) {
        const msg = "Missing valuation row id.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      setLastError("");

      try {
        const { error } = await supabase
          .from("fp_position_valuations")
          .delete()
          .eq("id", valuationId)
          .eq("fund_id", currentId)
          .eq("org_id", selectedOrg.id);

        if (error) throw error;

        return { ok: true };
      } catch (error) {
        console.error("Error deleting position valuation:", error);
        const msg = error?.message || "Unable to delete position valuation.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [currentId, profile?.id, selectedOrg?.id]
  );

  const loadPositionHistory = useCallback(
    async (positionId) => {
      const contextError = ensureActivityValuationContext({
        positionId,
        currentId,
        selectedOrg,
        profile,
      });

      if (contextError) {
        setLastError(contextError);
        return { ok: false, error: contextError, data: [] };
      }

      setLastError("");

      try {
        const [activityRes, valuationRes] = await Promise.all([
          supabase
            .from("fp_position_activity")
            .select("*")
            .eq("position_id", positionId)
            .eq("fund_id", currentId)
            .eq("org_id", selectedOrg.id)
            .order("activity_date", { ascending: false })
            .order("created_at", { ascending: false }),
          supabase
            .from("fp_position_valuations")
            .select("*")
            .eq("position_id", positionId)
            .eq("fund_id", currentId)
            .eq("org_id", selectedOrg.id)
            .order("valuation_date", { ascending: false })
            .order("created_at", { ascending: false }),
        ]);

        if (activityRes.error) throw activityRes.error;
        if (valuationRes.error) throw valuationRes.error;

        const positionById = new Map(
          loadedPositionsRef.current.map((position) => [position.id, position])
        );
        const activityRows = (Array.isArray(activityRes.data)
          ? activityRes.data
          : []
        ).map((row) => ({
          ...enrichScheduledCashflowRow(row, positionById),
          history_type: "activity",
          history_date: row.activity_date,
        }));
        const enrichedActivityRows = await enrichManualTransferRows({
          rows: activityRows,
          orgId: selectedOrg.id,
        });

        const valuationRows = (Array.isArray(valuationRes.data)
          ? valuationRes.data
          : []
        ).map((row) => ({
          ...row,
          history_type: "valuation",
          history_date: row.valuation_date,
        }));

        const data = [...enrichedActivityRows, ...valuationRows].sort((a, b) => {
          const dateCompare = String(b.history_date || "").localeCompare(
            String(a.history_date || "")
          );
          if (dateCompare) return dateCompare;
          return String(b.created_at || "").localeCompare(
            String(a.created_at || "")
          );
        });

        return { ok: true, data };
      } catch (error) {
        console.error("Error loading position history:", error);
        const msg = error?.message || "Unable to load position history.";
        setLastError(msg);
        return { ok: false, error: msg, data: [] };
      }
    },
    [currentId, profile, selectedOrg]
  );

  const saveLgipMonthEnd = useCallback(
    async (positionId, { activity, valuation } = {}) => {
      // TODO: Move this to a single Supabase RPC/transaction so both rows commit atomically.
      const activityResult = await savePositionActivity(positionId, activity);
      if (!activityResult.ok) return activityResult;

      const valuationResult = await savePositionValuation(positionId, valuation);
      if (!valuationResult.ok) return valuationResult;

      return { ok: true };
    },
    [savePositionActivity, savePositionValuation]
  );

  const generateSlgsDemandAccruals = useCallback(
    async (positionId, { endDate } = {}) => {
      const contextError = ensureActivityValuationContext({
        positionId,
        currentId,
        selectedOrg,
        profile,
      });

      if (contextError) {
        setLastError(contextError);
        return { ok: false, error: contextError };
      }

      if (!endDate) {
        const msg = "Select an end date for SLGS Demand Deposit accruals.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      setLastError("");

      try {
        const { data, error } = await supabase.rpc(
          "fp_generate_slgs_demand_accruals",
          {
            p_position_id: positionId,
            p_end_date: endDate,
          }
        );

        if (error) throw error;

        return { ok: true, data: Array.isArray(data) ? data : [] };
      } catch (error) {
        console.error("Error generating SLGS Demand Deposit accruals:", error);
        const msg =
          error?.message || "Unable to generate SLGS Demand Deposit accruals.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [currentId, profile, selectedOrg]
  );

  const getSlgsDemandAccrualRecalcRange = useCallback(
    async (positionId, { fromDate } = {}) => {
      const contextError = ensureActivityValuationContext({
        positionId,
        currentId,
        selectedOrg,
        profile,
      });

      if (contextError) {
        setLastError(contextError);
        return { ok: false, error: contextError, data: [] };
      }

      if (!fromDate) {
        const msg = "Select a start date for SLGS Demand Deposit recalculation.";
        setLastError(msg);
        return { ok: false, error: msg, data: [] };
      }

      setLastError("");

      try {
        const { data, error } = await supabase.rpc(
          "fp_slgs_demand_accrual_recalc_range",
          {
            p_position_id: positionId,
            p_from_date: fromDate,
          }
        );

        if (error) throw error;

        return { ok: true, data: Array.isArray(data) ? data : [] };
      } catch (error) {
        console.error("Error checking SLGS Demand Deposit recalculation range:", error);
        const msg =
          error?.message ||
          "Unable to check SLGS Demand Deposit recalculation range.";
        setLastError(msg);
        return { ok: false, error: msg, data: [] };
      }
    },
    [currentId, profile, selectedOrg]
  );

  const recalculateSlgsDemandAccruals = useCallback(
    async (positionId, { fromDate, endDate } = {}) => {
      const contextError = ensureActivityValuationContext({
        positionId,
        currentId,
        selectedOrg,
        profile,
      });

      if (contextError) {
        setLastError(contextError);
        return { ok: false, error: contextError };
      }

      if (!fromDate || !endDate) {
        const msg = "Select a date range for SLGS Demand Deposit recalculation.";
        setLastError(msg);
        return { ok: false, error: msg };
      }

      setLastError("");

      try {
        const { data, error } = await supabase.rpc(
          "fp_recalculate_slgs_demand_accruals",
          {
            p_position_id: positionId,
            p_from_date: fromDate,
            p_end_date: endDate,
          }
        );

        if (error) throw error;

        return { ok: true, data: Array.isArray(data) ? data : [] };
      } catch (error) {
        console.error("Error recalculating SLGS Demand Deposit accruals:", error);
        const msg =
          error?.message || "Unable to recalculate SLGS Demand Deposit accruals.";
        setLastError(msg);
        return { ok: false, error: msg };
      }
    },
    [currentId, profile, selectedOrg]
  );

  useEffect(() => {
    refreshPortfolios();
  }, [refreshPortfolios]);

  useEffect(() => {
    clearAllState();
  }, [clearAllState, selectedOrg?.id]);

  const value = useMemo(
    () => ({
      currentId,
      currentName,
      description,
      fundType,
      portfolioId,
      portfolioIds,
      sharing,
      loadedSharing,
      createdBy,
      currentOwner,
      yieldMax,
      sweepPositionId,
      portfolios,
      positions,
      isOwner,
      canEdit,
      canDelete,
      canChangeSharing,
      isReadOnly,
      isLocked,
      isDirty,
      isLoadingFund,
      isLoadingPortfolios,
      isSaving,
      lastError,
      lastSavedAt,
      selectedOrg,

      setCurrentId,
      setCurrentName,
      setDescription,
      setFundType,
      setPortfolioId,
      setPortfolioIds,
      setSharing,
      setYieldMax,
      setSweepPositionId,
      setPositions,
      setPositionsFromCommittedHistory,

      resetFund,
      loadFund,
      saveFund,
      saveAsFund,
      deleteFund,
      refreshPortfolios,
      createPortfolioGroup,
      loadTransferDestinationPositions,
      savePositionActivity,
      saveManualTransfer,
      savePositionValuation,
      updatePositionActivity,
      updateManualTransfer,
      updatePositionValuation,
      deletePositionActivity,
      deletePositionValuation,
      loadPositionHistory,
      saveLgipMonthEnd,
      generateSlgsDemandAccruals,
      getSlgsDemandAccrualRecalcRange,
      recalculateSlgsDemandAccruals,
      markDirty,

      makeBlankPosition,
      buildPositionFromDb,
    }),
    [
      currentId,
      currentName,
      description,
      fundType,
      portfolioId,
      portfolioIds,
      loadedSharing,
      createdBy,
      currentOwner,
      yieldMax,
      sweepPositionId,
      portfolios,
      sharing,
      positions,
      isOwner,
      canEdit,
      canDelete,
      canChangeSharing,
      isReadOnly,
      isLocked,
      isDirty,
      isLoadingFund,
      isLoadingPortfolios,
      isSaving,
      lastError,
      lastSavedAt,
      selectedOrg,
      setCurrentId,
      setCurrentName,
      setDescription,
      setFundType,
      setPortfolioId,
      setPortfolioIds,
      setSharing,
      setYieldMax,
      setSweepPositionId,
      setPositions,
      setPositionsFromCommittedHistory,
      resetFund,
      loadFund,
      saveFund,
      saveAsFund,
      deleteFund,
      refreshPortfolios,
      createPortfolioGroup,
      loadTransferDestinationPositions,
      savePositionActivity,
      saveManualTransfer,
      savePositionValuation,
      updatePositionActivity,
      updateManualTransfer,
      updatePositionValuation,
      deletePositionActivity,
      deletePositionValuation,
      loadPositionHistory,
      saveLgipMonthEnd,
      generateSlgsDemandAccruals,
      getSlgsDemandAccrualRecalcRange,
      recalculateSlgsDemandAccruals,
      markDirty,
    ]
  );

  return <FundsContext.Provider value={value}>{children}</FundsContext.Provider>;
}

export default FundsProvider;
